// The colour field behind the glass.
//
// Chips are the crew: translucent, different sizes, drifting, some bleeding off
// the edge, because we are spread out and doing our own thing. One motif per
// app-open, never mixed, so today is all circles and tomorrow is all pills and
// the app quietly feels different each time you come back.
//
// Pure on purpose: the shapes are decided here and painted by CSS, so "never
// mixed" and "always off the edge" can be tested rather than eyeballed.

export type Motif = "circles" | "pills" | "strokes";

export const MOTIFS: Motif[] = ["circles", "pills", "strokes"];

// The crew's colours, as seen through frosted glass.
export const CHIP_COLORS = [
  "123, 116, 230", // violet
  "53, 166, 166",  // teal
  "154, 114, 200", // orchid
  "46, 156, 116",  // green
  "240, 103, 58",  // orange
  "176, 113, 74",  // clay
  "231, 190, 74",  // gold
  "212, 83, 126",  // rose
];

export interface Blob {
  color: string;
  /** Percent of the field, so the shapes move with the screen. */
  left: number;
  top: number;
  width: number;
  height: number;
  rotate: number;
  /** Seconds. Every blob drifts on its own clock or they pulse as one. */
  duration: number;
  delay: number;
  radius: string;
}

const SHAPES: Record<Motif, { ratio: [number, number]; radius: string; size: [number, number] }> = {
  circles: { ratio: [0.72, 1.18], radius: "46% 54% 58% 42% / 52% 46% 54% 48%", size: [96, 190] },
  pills: { ratio: [0.34, 0.5], radius: "999px", size: [130, 240] },
  strokes: { ratio: [0.08, 0.15], radius: "999px", size: [150, 280] },
};

export function pickMotif(rng: () => number = Math.random): Motif {
  return MOTIFS[Math.floor(rng() * MOTIFS.length) % MOTIFS.length];
}

export function blobField(motif: Motif, count: number, rng: () => number = Math.random): Blob[] {
  const shape = SHAPES[motif];
  const span = (a: number, b: number) => a + rng() * (b - a);
  return Array.from({ length: count }, (_, i) => {
    const width = span(shape.size[0], shape.size[1]);
    return {
      color: CHIP_COLORS[i % CHIP_COLORS.length],
      // Deliberately allowed past every edge: some of the crew is always
      // half out of frame.
      left: span(-18, 86),
      top: span(-14, 84),
      width,
      height: width * span(shape.ratio[0], shape.ratio[1]),
      rotate: span(-40, 40),
      duration: span(9, 16),
      delay: -span(0, 12),
      radius: shape.radius,
    };
  });
}
